/**
 * Display formatting utilities — shared across all booking surfaces.
 *
 * Consolidates duplicated formatters from:
 * - packages/template-components/src/booking/utils.ts (formatMinutes)
 *
 * No framework dependencies — pure string formatting functions.
 */

/**
 * Convert 24-hour time string (e.g. "14:30") to display format.
 * Defaults to 12-hour ("2:30 pm"). Pass `use24Hour: true` for "14:30".
 */
export function formatTime12(
  time24: string,
  options?: { use24Hour?: boolean },
): string {
  if (options?.use24Hour) return time24;
  const [hh, mm] = time24.split(":");
  const h = parseInt(hh, 10);
  const suffix = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${mm} ${suffix}`;
}

/**
 * Format a monetary amount for display.
 *
 * The DB stores prices as Decimal(10,2) and the API returns plain numbers in
 * the major unit (e.g. dollars) — so callers always pass major-unit values.
 *
 * Supports multi-currency via optional `currency` (ISO 4217) and `locale`.
 * Defaults to USD / en-US for backward compatibility.
 * Zero-decimal currencies like JPY are handled automatically by Intl.
 */
export function formatPrice(
  amount: number,
  options?: { currency?: string; locale?: string },
): string {
  const currency = options?.currency ?? "USD";
  const locale = options?.locale ?? "en-US";
  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      // Drop fractional digits when the amount has no cents (or for zero-decimal currencies)
      minimumFractionDigits: 0,
      maximumFractionDigits: new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
      }).resolvedOptions().maximumFractionDigits,
    }).format(amount);
    return formatted;
  } catch {
    // Fallback for unknown currency codes
    const fraction = Math.round(amount * 100) % 100;
    return `${currency} ${amount.toFixed(fraction === 0 ? 0 : 2)}`;
  }
}

/**
 * Format a duration in minutes to a compact string.
 * Examples: 45 → "45 mins", 60 → "1h", 90 → "1h 30m"
 */
export function formatDuration(minutes: number): string {
  if (!minutes) return "0 mins";
  if (minutes < 60) return `${minutes} mins`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/**
 * Format a duration in minutes to a longer display string.
 * Examples: 45 → "45 mins", 60 → "1 hr", 90 → "1 hr 30 mins"
 *
 * This is the "verbose" variant used in the template-components booking UI.
 * Returns null for falsy input.
 */
export function formatMinutes(minutes: number): string | null {
  if (!minutes) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;

  if (h && m) {
    return `${h} hr${h > 1 ? "s" : ""} ${m} min${m > 1 ? "s" : ""}`;
  }
  if (h) {
    return `${h} hr${h > 1 ? "s" : ""}`;
  }
  if (m) {
    return `${m} min${m > 1 ? "s" : ""}`;
  }
  return "0 mins";
}

/**
 * Format a YYYY-MM-DD date string to a short display string.
 * Example (en-US): "2026-02-18" → "Wed, Feb 18, 2026"
 * Example (ja-JP): "2026-02-18" → "2026/2/18(水)"
 */
export function formatDateShort(dateStr: string, locale?: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString(locale ?? "en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format a YYYY-MM-DD date string to a long display string.
 * Example (en-US): "2026-02-18" → "Wednesday, February 18, 2026"
 * Example (ja-JP): "2026-02-18" → "2026年2月18日水曜日"
 */
export function formatDateLong(dateStr: string, locale?: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString(locale ?? "en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Convert a Date object to a YYYY-MM-DD string.
 *
 * Uses local date parts (not UTC) to avoid timezone-shift issues.
 */
export function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// ── Quote duration options ─────────────────────────────────────────────

/** Duration presets for the "Send Quote" modal (dashboard + staff). */
export const QUOTE_DURATION_OPTIONS: ReadonlyArray<{
  label: string;
  value: number;
}> = [
  { label: "30 minutes", value: 30 },
  { label: "45 minutes", value: 45 },
  { label: "1 hour", value: 60 },
  { label: "1.5 hours", value: 90 },
  { label: "2 hours", value: 120 },
  { label: "2.5 hours", value: 150 },
  { label: "3 hours", value: 180 },
  { label: "3.5 hours", value: 210 },
  { label: "4 hours", value: 240 },
  { label: "4.5 hours", value: 270 },
  { label: "5 hours", value: 300 },
  { label: "5.5 hours", value: 330 },
  { label: "6 hours", value: 360 },
  { label: "6.5 hours", value: 390 },
  { label: "7 hours", value: 420 },
  { label: "7.5 hours", value: 450 },
  { label: "8 hours", value: 480 },
];

/** Format a duration in minutes to a compact dropdown label (e.g. "1 hr 30 min"). */
export function getDurationLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} hr`;
  return `${hours} hr ${mins} min`;
}
